import { UseFormGetValues } from "react-hook-form";
import { format, isBefore, isValid, parse, startOfToday } from "date-fns";

import { UserType } from "src/types/users";
import {
  AddCaseProps,
  CaseAttributesProps,
  CaseDoctorProps,
  InsuranceAttributesProps,
  PatientAddress,
  PatientAttributes,
  PatientPhoneNumber,
  PrescriptionAttributesType,
} from "src/pages/addCase/types";
import { CaseCloneResponseType } from "src/pages/cloneCase/types";
import { SHIP_TO } from "src/constants/cases";

function detectDateFormat(dateString: string) {
  // Extend this list with the formats you expect
  const possibleFormats = [
    "yyyy-MM-dd",
    "dd/MM/yyyy",
    "MM/dd/yyyy",
    "dd-MM-yyyy",
    "MM-dd-yyyy",
    "yyyy/MM/dd",
    "dd MMM yyyy",
    "MMM dd, yyyy",
  ];

  for (const fmt of possibleFormats) {
    try {
      const parsed = parse(dateString, fmt, new Date());
      // If parsing succeeded and returned a valid Date
      if (isValid(parsed)) {
        return fmt;
      }
    } catch (err) {
      // ignore failed format
    }
  }

  return null; // No matching format found
}

export function populatingCloneCase(
  { data }: CaseCloneResponseType,
  userData: UserType,
  getValues: UseFormGetValues<AddCaseProps>,
): AddCaseProps {
  const caseFormData: AddCaseProps = {} as AddCaseProps;

  if (data.case) {
    caseFormData.mos = getValues("mos");
    caseFormData.user_id = userData ? userData.id : 0;
    caseFormData.creator_name = userData ? userData.name : "";
    caseFormData.submission_source = data.case.submission_source || "provider";
    caseFormData.category = "rx";
    caseFormData.uploaded_documents = [];
  }

  caseFormData.api_pa_approved = data.pa_approved;

  caseFormData.case_attributes = {} as CaseAttributesProps;
  if (data.case_attributes.length > 0) {
    data.case_attributes.forEach((item) => {
      if (item.name === "pa_approved") {
        caseFormData.case_attributes.pa_approved = item.value;
      }
      if (item.name === "pa_approved_date") {
        caseFormData.case_attributes.pa_approved_date = item.value;
      }
    });
  }

  // if(caseFormData.patient_attributes){
  //   if(data.caregiver_name && data.caregiver_name.length > 0){
  //     caseFormData.patient_attributes.caregiver_name = data.caregiver_name;
  //   }
  //   if(data.caregiver_number && data.caregiver_number.length > 0){
  //     caseFormData.patient_attributes.caregiver_number = data.caregiver_number;
  //   }
  //   if(data.relation_to_patient && data.relation_to_patient.length > 0){
  //     caseFormData.patient_attributes.relation_to_patient = data.relation_to_patient;
  //   }
  // }

  if (data.soc) {
    const isActualShipTo = SHIP_TO.find(
      (soc) => soc?.value === data.soc[0]?.value,
    );
    if (isActualShipTo) {
      caseFormData.case_attributes.ship_to = data.soc[0].value;
    } else {
      caseFormData.case_attributes.ship_to = "other";
    }
  }

  caseFormData.prescription_attributes = {
    written_date: format(new Date(), "MM/dd/yyyy"),
  } as PrescriptionAttributesType;

  if (data.prescription) {
    const diagnosis = data.prescription.diagnosis_code;
    caseFormData.prescription_attributes.diagnosis_code =
      diagnosis !== "" ? diagnosis.split(",") : [];
    caseFormData.prescription_attributes.note = data.prescription.note;
    caseFormData.prescription_attributes.direction =
      data.prescription.direction;
    caseFormData.prescription_attributes.injection_site =
      data.prescription.qty || "0";
    caseFormData.prescription_attributes.refills_authorized =
      data.prescription.refills_authorized || 0;
    const drug_name = data.prescription.product;
    //console.log("drug_name", drug_name)
    caseFormData.prescription_attributes.drug_name = drug_name;
    caseFormData.prescription_attributes.written_date = format(
      new Date(),
      "MM/dd/yyyy",
    );
  }

  if (data.prescription_attributes) {
    const cp_diuretics: any = {
      diuretics_tried: {
        furosemide: false,
        furosemide_props: {
          "20mg": false,
          "40mg": false,
          "80mg": false,
          result_of_use: "",
          hospitalization_date: "",
        },
        torsemide: false,
        torsemide_props: {
          "5mg": false,
          "10mg": false,
          "20mg": false,
          "40mg": false,
          "60mg": false,
          "100mg": false,
          result_of_use: "",
          hospitalization_date: "",
        },
        bumetanide: false,
        bumetanide_props: {
          "05mg": false,
          "1mg": false,
          "2mg": false,
          "3mg": false,
          "4mg": false,
          result_of_use: "",
          hospitalization_date: "",
        },
      },
      creatinine_clearance: "",
      eGFR: "",
    };
    data.prescription_attributes.forEach((attr) => {
      if (attr.name === "scheduled_date_of_service") {
        if (attr.value) {
          const dateformat = detectDateFormat(attr.value);
          if (dateformat) {
            const scheduled_date = parse(attr.value, dateformat, new Date());
            if (!isBefore(scheduled_date, startOfToday())) {
              caseFormData.prescription_attributes.scheduled_date_of_service =
                format(parse(attr.value, dateformat, new Date()), "yyyy-MM-dd");
            }
          }
        }
      } else if (attr.name === "condition") {
        caseFormData.prescription_attributes.condition = attr.value || "";
      } else if (attr.name === "frequency_of_use") {
        const [frequency, ...otherText] = String(attr.value || "").split("-");
        //console.log("otherText", (otherText));
        caseFormData.prescription_attributes.frequency_of_use =
          frequency?.trim() || "";

        caseFormData.prescription_attributes.other_frequency_of_use = (otherText).toString()
      } else if (attr.name === "daily_dose") {
        const [dailyDose, ...otherText] = String(attr.value || "").split("-");
        caseFormData.prescription_attributes.daily_dose =
          dailyDose?.trim() || "";
        caseFormData.prescription_attributes.other_daily_dose = otherText.join("-") || "";
      }  else if (attr.name === "review_type") {
        caseFormData.prescription_attributes.review_type = attr.value || "";
      } else if (attr.name === "supervising_md_firstname") {
        caseFormData.prescription_attributes.supervising_md_firstname = attr.value || "";
      }else if (attr.name === "supervising_md_lastname") {
        caseFormData.prescription_attributes.supervising_md_lastname = attr.value || "";
      }
       else if (attr.name === "bumetanide_dosage") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;
        attr.value &&
          attr.value.split(",").forEach((dose: string) => {
            if (
              dose in cp_diuretics.diuretics_tried.bumetanide_props ||
              dose.includes("0.5")
            ) {
              if (dose.includes("0.5"))
                cp_diuretics.diuretics_tried.bumetanide_props["05mg"] = true;
              else cp_diuretics.diuretics_tried.bumetanide_props[dose] = true;
            } else {
              cp_diuretics.diuretics_tried.bumetanide_props.other_dose = true;
              cp_diuretics.diuretics_tried.bumetanide_props.other_dosage_value =
                dose.replace("mg", "");
            }
          });
      } else if (attr.name === "bumetanide_result_of_use") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;

        cp_diuretics.diuretics_tried.bumetanide_props.result_of_use =
          attr.value;
      } else if (attr.name === "bumetanide_hospitalization_date") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;

        cp_diuretics.diuretics_tried.bumetanide_props.hospitalization_date =
          attr.value;
      } else if (attr.name === "bumetanide_other") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;

        cp_diuretics.diuretics_tried.bumetanide_props.other = attr.value;
      } else if (attr.name === "furosemide_dosage") {
        attr.value &&
          attr.value.split(",").forEach((dose: string) => {
            cp_diuretics.diuretics_tried.furosemide =
              attr.value && attr.value.length > 0 ? true : false;
            if (dose in cp_diuretics.diuretics_tried.furosemide_props) {
              cp_diuretics.diuretics_tried.furosemide_props[dose] = true;
            } else {
              cp_diuretics.diuretics_tried.furosemide_props.other_dose = true;
              cp_diuretics.diuretics_tried.furosemide_props.other_dosage_value =
                dose.replace("mg", "");
            }
          });
      } else if (attr.name === "furosemide_result_of_use") {
        cp_diuretics.diuretics_tried.furosemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.furosemide_props.result_of_use =
          attr.value;
      } else if (attr.name === "furosemide_hospitalization_date") {
        cp_diuretics.diuretics_tried.furosemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.furosemide_props.hospitalization_date =
          attr.value;
      } else if (attr.name === "furosemide_other") {
        cp_diuretics.diuretics_tried.furosemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.furosemide_props.other = attr.value;
      } else if (attr.name === "torsemide_dosage") {
        attr.value &&
          attr.value.split(",").forEach((dose: string) => {
            cp_diuretics.diuretics_tried.torsemide =
              attr.value && attr.value.length > 0 ? true : false;
            if (dose in cp_diuretics.diuretics_tried.torsemide_props) {
              cp_diuretics.diuretics_tried.torsemide_props[dose] = true;
            } else {
              cp_diuretics.diuretics_tried.torsemide_props.other_dose = true;
              cp_diuretics.diuretics_tried.torsemide_props.other_dosage_value =
                dose.replace("mg", "");
            }
          });
      } else if (attr.name === "torsemide_result_of_use") {
        cp_diuretics.diuretics_tried.torsemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.torsemide_props.result_of_use = attr.value;
      } else if (attr.name === "torsemide_hospitalization_date") {
        cp_diuretics.diuretics_tried.torsemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.torsemide_props.hospitalization_date =
          attr.value;
      } else if (attr.name === "torsemide_other") {
        cp_diuretics.diuretics_tried.torsemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.torsemide_props.other = attr.value;
      } else if (attr.name === "creatinine_clearance") {
        cp_diuretics.creatinine_clearance = attr.value;
      } else if (attr.name === "glomerular_filtration") {
        cp_diuretics.eGFR = attr.value;
      }
    });
    caseFormData.prescription_attributes.cp_diuretics = cp_diuretics;
    caseFormData.prescription_attributes.written_date = format(
      new Date(),
      "MM/dd/yyyy",
    );
  }

  //transformation logic for cp_diuretics
  if (data.prescription_attributes) {
    const cp_diuretics: any = {
      diuretics_tried: {
        furosemide: false,
        furosemide_props: {
          "20mg": false,
          "40mg": false,
          "80mg": false,
          result_of_use: "",
          hospitalization_date: "",
        },
        torsemide: false,
        torsemide_props: {
          "5mg": false,
          "10mg": false,
          "20mg": false,
          "40mg": false,
          "60mg": false,
          "100mg": false,
          result_of_use: "",
          hospitalization_date: "",
        },
        bumetanide: false,
        bumetanide_props: {
          "05mg": false,
          "1mg": false,
          "2mg": false,
          "3mg": false,
          "4mg": false,
          result_of_use: "",
          hospitalization_date: "",
        },
      },
      creatinine_clearance: "",
      eGFR: "",
    };
    data.prescription_attributes.forEach((attr) => {
      if (attr.name === "scheduled_date_of_service") {
        if (attr.value) {
          const dateformat = detectDateFormat(attr.value);
          if (dateformat) {
            const scheduled_date = parse(attr.value, dateformat, new Date());
            if (!isBefore(scheduled_date, startOfToday())) {
              caseFormData.prescription_attributes.scheduled_date_of_service =
                format(parse(attr.value, dateformat, new Date()), "yyyy-MM-dd");
            }
          }
        }
      } else if (attr.name === "condition") {
        caseFormData.prescription_attributes.condition = attr.value || "";
      } else if (attr.name === "review_type") {
        caseFormData.prescription_attributes.review_type = attr.value || "";
      }
      // else if (attr.name === "bumetanide_dosage") {
      //   cp_diuretics.diuretics_tried.bumetanide = attr.value && attr.value.length > 0 ? true : false;
      //  attr.value && attr.value.split(",").forEach((dose: string) => {

      //     if (dose in cp_diuretics.diuretics_tried.bumetanide_props || dose.includes("0.5")) {
      //       if (dose.includes("0.5"))
      //         cp_diuretics.diuretics_tried.bumetanide_props["05mg"] = true;
      //       else
      //         cp_diuretics.diuretics_tried.bumetanide_props[dose] = true;
      //     }
      //   });
      // }
      else if (attr.name === "bumetanide_dosage") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;
        attr.value &&
          attr.value.split(",").forEach((dose: string) => {
            if (
              dose in cp_diuretics.diuretics_tried.bumetanide_props ||
              dose.includes("0.5")
            ) {
              if (dose.includes("0.5"))
                cp_diuretics.diuretics_tried.bumetanide_props["05mg"] = true;
              else cp_diuretics.diuretics_tried.bumetanide_props[dose] = true;
            } else {
              cp_diuretics.diuretics_tried.bumetanide_props.other_dose = true;
              cp_diuretics.diuretics_tried.bumetanide_props.other_dosage_value =
                dose.replace("mg", "");
            }
          });
      } else if (attr.name === "bumetanide_result_of_use") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;

        cp_diuretics.diuretics_tried.bumetanide_props.result_of_use =
          attr.value;
      } else if (attr.name === "bumetanide_hospitalization_date") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;

        cp_diuretics.diuretics_tried.bumetanide_props.hospitalization_date =
          attr.value;
      } else if (attr.name === "bumetanide_other") {
        cp_diuretics.diuretics_tried.bumetanide =
          attr.value && attr.value.length > 0 ? true : false;

        cp_diuretics.diuretics_tried.bumetanide_props.other = attr.value;
      }
      // else if (attr.name === "furosemide_dosage") {
      //   attr.value && attr.value.split(",").forEach((dose: string) => {
      //     cp_diuretics.diuretics_tried.furosemide = attr.value && attr.value.length > 0 ? true : false;;
      //     if (dose in cp_diuretics.diuretics_tried.furosemide_props) {
      //       cp_diuretics.diuretics_tried.furosemide_props[dose] = true;
      //     }
      //   });
      // }
      else if (attr.name === "furosemide_dosage") {
        attr.value &&
          attr.value.split(",").forEach((dose: string) => {
            cp_diuretics.diuretics_tried.furosemide =
              attr.value && attr.value.length > 0 ? true : false;
            if (dose in cp_diuretics.diuretics_tried.furosemide_props) {
              cp_diuretics.diuretics_tried.furosemide_props[dose] = true;
            } else {
              cp_diuretics.diuretics_tried.furosemide_props.other_dose = true;
              cp_diuretics.diuretics_tried.furosemide_props.other_dosage_value =
                dose.replace("mg", "");
            }
          });
      } else if (attr.name === "furosemide_result_of_use") {
        cp_diuretics.diuretics_tried.furosemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.furosemide_props.result_of_use =
          attr.value;
      } else if (attr.name === "furosemide_hospitalization_date") {
        cp_diuretics.diuretics_tried.furosemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.furosemide_props.hospitalization_date =
          attr.value;
      } else if (attr.name === "furosemide_other") {
        cp_diuretics.diuretics_tried.furosemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.furosemide_props.other = attr.value;
      }
      // else if (attr.name === "torsemide_dosage") {

      //  attr.value && attr.value.split(",").forEach((dose: string) => {
      //   cp_diuretics.diuretics_tried.torsemide = attr.value && attr.value.length > 0 ? true : false;;
      //     if (dose in cp_diuretics.diuretics_tried.torsemide_props) {
      //       cp_diuretics.diuretics_tried.torsemide_props[dose] = true;
      //     }
      //   });
      // }
      else if (attr.name === "torsemide_dosage") {
        attr.value &&
          attr.value.split(",").forEach((dose: string) => {
            cp_diuretics.diuretics_tried.torsemide =
              attr.value && attr.value.length > 0 ? true : false;
            if (dose in cp_diuretics.diuretics_tried.torsemide_props) {
              cp_diuretics.diuretics_tried.torsemide_props[dose] = true;
            } else {
              cp_diuretics.diuretics_tried.torsemide_props.other_dose = true;
              cp_diuretics.diuretics_tried.torsemide_props.other_dosage_value =
                dose.replace("mg", "");
            }
          });
      } else if (attr.name === "torsemide_result_of_use") {
        cp_diuretics.diuretics_tried.torsemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.torsemide_props.result_of_use = attr.value;
      } else if (attr.name === "torsemide_hospitalization_date") {
        cp_diuretics.diuretics_tried.torsemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.torsemide_props.hospitalization_date =
          attr.value;
      } else if (attr.name === "torsemide_other") {
        cp_diuretics.diuretics_tried.torsemide =
          attr.value && attr.value.length > 0 ? true : false;
        cp_diuretics.diuretics_tried.torsemide_props.other = attr.value;
      } else if (attr.name === "creatinine_clearance") {
        cp_diuretics.creatinine_clearance = attr.value;
      } else if (attr.name === "glomerular_filtration") {
        cp_diuretics.eGFR = attr.value;
      }
    });
    caseFormData.prescription_attributes.cp_diuretics = cp_diuretics;
  }

  if (data.shipments && data.shipments.length > 0) {
    caseFormData.shipments_attributes = [
      {
        ship_to_street: data.shipments[0].ship_to_street,
        ship_to_suite: data.shipments[0].ship_to_suite,
        ship_to_city: data.shipments[0].ship_to_city,
        ship_to_state: data.shipments[0].ship_to_state,
        ship_to_zip: data.shipments[0].ship_to_zip,
      },
    ];
  }

  if (data.doctor) {
    caseFormData.doctor_firstlastname = `${data.doctor.firstname} ${data.doctor.lastname}`;
    caseFormData.doctor_fullname = `${data.doctor.firstname} ${
      data.doctor.middlename || ""
    } ${data.doctor.lastname}`;

    caseFormData.doctor_attributes = {
      practice_id: data.doctor.practice_id,
      global_doctor_id: data.doctor.global_doctor_id,
    } as CaseDoctorProps;
  }

  if (data.practice || data.practice_address) {
    caseFormData.practice_name = `${data.practice.name} - ${data.practice_address.full_address}`;
  }

  if (data.patient) {
    const { patient } = data;

    caseFormData.patient_firstlastname = `${patient.firstname} ${patient.lastname}`;
    caseFormData.patient_fullname = `${patient.firstname} ${
      patient.middlename || ""
    } ${patient.lastname}`;
    caseFormData.patient_dob = patient.dob;

    caseFormData.patient_attributes = {
      global_patient_id: patient.global_patient_id,
      title: patient.title || "",
      firstname: patient.firstname,
      middlename: patient.middlename,
      lastname: patient.lastname,
      gender: patient.gender,
      dob: patient.dob,
      ssn: patient.ssn,
      best_time_to_contact: patient.best_time_to_contact || "",
      // patient_email_id: null,
      // patient_email: data.email_address ? data.email_address.email : "",
      caregiver_name: data.caregiver_name ? data.caregiver_name : "",
      caregiver_phone_number: data.caregiver_phone_number
        ? data.caregiver_phone_number
        : "",
      relationship_to_patient: data.relationship_to_patient
        ? data.relationship_to_patient
        : "",
    } as PatientAttributes;
  }

  if (data.patient_phone_numbers && data.patient_phone_numbers.length > 0) {
    const patientAttributes =
      caseFormData.patient_attributes as PatientAttributes;
    patientAttributes.phone_numbers = [];

    data.patient_phone_numbers.forEach((phone, index) => {
      patientAttributes.phone_numbers[index] = {} as PatientPhoneNumber;
      patientAttributes.phone_numbers[index].number = phone.number.toString();
      patientAttributes.phone_numbers[index].phone_type =
        phone.phone_type || "cell";
      patientAttributes.phone_numbers[index].is_ok_to_leave_message =
        phone.is_ok_to_leave_message;
      patientAttributes.phone_numbers[index].is_preferred = phone.is_preferred;
      patientAttributes.phone_numbers[index].id = phone.phone_number_id;
    });
  }

  const patientAttributes =
    caseFormData.patient_attributes as PatientAttributes;
  patientAttributes.address = {
    address_name: "home",
  } as PatientAddress;
  if (data.patient_address) {
    patientAttributes.address.street = data.patient_address.street || "";
    patientAttributes.address.suite = data.patient_address.suite || "";
    patientAttributes.address.city = data.patient_address.city || "";
    patientAttributes.address.state = data.patient_address.state || "";
    patientAttributes.address.zip = data.patient_address.zip || "";
  }

  if (data.patient_entities_address) {
    patientAttributes.address.address_name =
      data.patient_entities_address.address_name || "";
  }

  caseFormData.insurances_attributes = [{} as InsuranceAttributesProps];
  if (data.insurances && data.insurances.length > 0) {
    data.insurances.forEach((insurance, index) => {
      if (caseFormData.insurances_attributes) {
        caseFormData.insurances_attributes[index] =
          {} as InsuranceAttributesProps;
        caseFormData.insurances_attributes[index].insurance_name =
          insurance.insurance_name;
        caseFormData.insurances_attributes[index].relation_to_patient =
          insurance.relation_to_patient;
        caseFormData.insurances_attributes[index].relation_firstname =
          insurance.relation_firstname || "";
        caseFormData.insurances_attributes[index].relation_middlename =
          insurance.relation_middlename || "";
        caseFormData.insurances_attributes[index].relation_lastname =
          insurance.relation_lastname || "";
        caseFormData.insurances_attributes[index].policy =
          insurance.policy || "";
        caseFormData.insurances_attributes[index].group_number =
          insurance.group_number || "";
        caseFormData.insurances_attributes[index].insurance_number =
          insurance.insurance_number || "";
        caseFormData.insurances_attributes[index].member_id =
          insurance.member_id || "";
        caseFormData.insurances_attributes[index].bin = insurance.bin || "";
        caseFormData.insurances_attributes[index].pcn = insurance.pcn || "";
        caseFormData.insurances_attributes[index].rx_group =
          insurance.rx_group || "";
        caseFormData.insurances_attributes[index].global_insurance_id =
          insurance.global_insurance_id
            ? insurance.global_insurance_id.toString()
            : null;
        caseFormData.insurances_attributes[index].is_primary_insurance =
          index === 0;
        caseFormData.insurances_attributes[index].insurance_priority = index;
        caseFormData.insurances_attributes[index].plan_type =
          insurance.plan_type || "";
      }
    });
  }

  return caseFormData;
}
